import React, { useState, useEffect, useRef, forwardRef } from "react";
import {
  HeroSection,
  WorksSection,
  FixedNav,
  Gallery,
  Footer,
  Toolkit,
} from '../components';
import { AllBlogs } from './AllBlogs';
import { NavLink } from "react-router-dom";
import { Contact } from "../components/Contact";
import { useMediaQuery } from "../hooks";

/* =====================
   Custom Cursor
===================== */
const CustomCursor = forwardRef(function CustomCursor(props, ref) {
  const [isOverLink, setIsOverLink] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const moveCursor = (e) => {
      if (!ref?.current) return;
      ref.current.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
      setIsVisible(true);
    };
    const onOver = (e) => setIsOverLink(!!e.target.closest?.('a, button, [role="button"]'));
    const onLeave = () => setIsVisible(false);

    window.addEventListener("mousemove", moveCursor);
    document.addEventListener("mouseover", onOver);
    document.documentElement.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("mousemove", moveCursor);
      document.removeEventListener("mouseover", onOver);
      document.documentElement.removeEventListener("mouseleave", onLeave);
    };
  }, [ref]);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="custom-cursor fixed top-0 left-0 w-0 h-0 pointer-events-none z-[9999] mix-blend-difference"
      style={{ opacity: isVisible ? 1 : 0 }}
    >
      <div
        className={`absolute -left-5 -top-5 w-10 h-10 border rounded-full transition-[transform,background-color] duration-300 ease-out ${isOverLink ? 'scale-150 bg-white' : 'scale-100'}`}
      />
    </div>
  );
});

/* =====================
   RevealWord Component
===================== */
export const RevealWord = ({ standard, hidden, className }) => {
  const wordRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  // The lens needs a mouse. On touch screens the phrase is plain bold text,
  // which also lets it wrap normally instead of forcing its own line.
  const canHover = useMediaQuery('(hover: hover) and (pointer: fine)');

  // 1. Set the lens size. 
  // IMPORTANT: Match 'lensRadius' to half the width of your CustomCursor (e.g., w-10 = 20px radius)
  const lensRadius = 19;
  const holeRadius = lensRadius + 1; // Slightly larger to ensure zero overlap

  // The lens is centred on the pointer itself (the cursor ring sits exactly
  // on the pointer too), and re-measured on scroll so it stays put when the
  // text moves under a still mouse.
  const pointerRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!hovered) return;
    const place = () => {
      if (!wordRef.current) return;
      const rect = wordRef.current.getBoundingClientRect();
      setCursorPos({ x: pointerRef.current.x - rect.left, y: pointerRef.current.y - rect.top });
    };
    const onMove = (e) => {
      pointerRef.current = { x: e.clientX, y: e.clientY };
      place();
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("scroll", place, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("scroll", place);
    };
  }, [hovered]);

  if (!canHover) return <span className={className}>{standard}</span>;

  return (
    <span
      ref={wordRef}
      onMouseEnter={(e) => {
        pointerRef.current = { x: e.clientX, y: e.clientY };
        const rect = e.currentTarget.getBoundingClientRect();
        setCursorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        setHovered(true);
      }}
      onMouseLeave={() => setHovered(false)}
      className={`inline-grid relative align-baseline ${className || ""}`}
      style={{ cursor: "none" }}
    >
      {/* STANDARD TEXT: Punched out by the cursor */}
      <span
        style={{
          gridArea: "1 / 1",
          WebkitMaskImage: hovered
            ? `radial-gradient(circle ${holeRadius}px at ${cursorPos.x}px ${cursorPos.y}px, transparent 100%, black 100%)`
            : "none",
        }}
      >
        {standard}
      </span>

      {/* HIDDEN TEXT: Only revealed inside the circle */}
      <span
        className={`text-[#ff6347] font-black whitespace-nowrap `}
        style={{
          gridArea: "1 / 1",
          pointerEvents: "none",
          zIndex: 10,
          opacity: hovered ? 1 : 0,
          WebkitClipPath: `circle(${lensRadius}px at ${cursorPos.x}px ${cursorPos.y}px)`,
          clipPath: `circle(${lensRadius}px at ${cursorPos.x}px ${cursorPos.y}px)`,
        }}
      >
        {hidden}
      </span>

      {/* GHOST SPACER: Keeps the sentence from jumping */}
      <span className="invisible select-none pointer-events-none" style={{ gridArea: "1 / 1" }}>
        {standard.length > hidden.length ? standard : hidden}
      </span>
    </span>
  );
};

/* =====================
   Single Page
===================== */
export const SinglePage = () => {
  const cursorRef = useRef(null);


  const nameRef = useRef(null);
  const helloRef = useRef(null);
  const isMobile = useMediaQuery('(max-width: 767px)');
  // True once the falling hero name reaches its spot in the About heading.
  // Kept as a boolean so the page only re-renders when it flips, not on
  // every scroll event.
  const [nameLanded, setNameLanded] = useState(false);

  useEffect(() => {
    let rafId = null;
    const update = () => {
      rafId = null;
      const target = nameRef.current;
      if (!target) return;
      const rect = target.getBoundingClientRect();
      setNameLanded(rect.top + rect.height / 2 <= window.innerHeight / 2);
    };
    const onScroll = () => {
      if (rafId === null) rafId = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  const showHello = nameLanded || isMobile;

  return (
    <>
      <div className="min-h-screen bg-custom-background custom-cursor-area">
        <CustomCursor ref={cursorRef} /> {/* Fixed Navigation */} <FixedNav /> {/* Hero Section with Falling Name Animation */}
        <HeroSection />
        <section id="about" className="min-h-screen relative text-charcoal py-20 px-5 sm:px-10 lg:px-20">
          <div className='fade-in max-w-7xl mx-auto animate-fade-in-up'> {/* Hero intro - Keep what you love */}
            <div className='mb-24'> <div className='overflow-hidden mb-12' style={{ height: '16vw' }}>
              <h2 ref={helloRef} className='font-grandslang text-[14vw] leading-normal intro-line slide ' style={{ transform: showHello ? 'translateY(0)' : 'translateY(100%)', opacity: showHello ? 1 : 0, transition: 'transform 0.8s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.8s ease-out' }} > Hello. </h2>
            </div>
              <p className='font-grandslang text-[12vw] md:text-[90px] leading-[1.1em] mb-6'> I'm{' '}
                <span ref={nameRef} id="name-destination" className="inline-block relative text-[#ff6347]"> Daverose </span> {' '}Tholley.
              </p>
              <p className='text-xl md:text-2xl text-charcoal/70 font-light leading-relaxed max-w-3xl'> Sierra Leonean soul, Ghana-based. Merging engineering with social impact and design. </p>
            </div>
            {/* New layout - split into interesting blocks */}
            <div className='grid h-min lg:grid-cols-3 gap-12 lg:gap-16 mb-20 stagger-children'> {/* Block 1: Image */} <div className='lg:col-span-1'>
              <div className="lg:col-span-5 relative group">
                <div className="absolute -inset-4 border border-charcoal/5 pointer-events-none group-hover:-inset-2 transition-all duration-700" />
                <div className="overflow-hidden bg-[#eee] aspect-[4/5]">
                  <img src="https://res.cloudinary.com/daj0ik8rv/image/upload/v1766790414/PXL_20250623_062943315.PORTRAIT_lxlsvj.jpg" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000 scale-105 group-hover:scale-100" alt="Portrait of Daverose Tholley" loading="lazy" decoding="async" />
                </div>
                <div className="eyebrow mt-8 flex justify-between items-center">
                  <span>Portrait — 01</span> <span>2025</span>
                </div>
              </div>
            </div>
              {/* Block 2: Current roles + Quick bio */}
              <div className="lg:col-span-2 flex flex-col justify-center space-y-16">
                <div className="space-y-12">
                  <div>
                    <h3 className="eyebrow mb-12">Beyond the CV</h3>
                    <div className="space-y-10">
                      <div className="border-l border-charcoal/10 pl-8 space-y-8">
                        <div className="max-w-4xl">
                          <p className="text-xl md:text-2xl text-charcoal/70 leading-relaxed font-light">
                            I have a thing for {' '}
                            <RevealWord
                              standard="organized chaos."
                              hidden="the loud stuff."
                              className="text-charcoal font-semibold"
                            />
                            {' '}I need a grid, not to box things in, but so the{' '}
                            <RevealWord
                              standard="energy has a place to live."
                              hidden="loudness has a reason."
                              className="text-charcoal font-semibold"
                            />
                            <br /><br />

                            Lately, that looks like obsessing over{' '}
                            <RevealWord
                              standard="the sculpting of a garment"
                              hidden="the folds, the layers, I love!"
                              className="text-charcoal font-semibold"
                            />
                            {' '}or the way a room is put together. I love seeing sheer volume folded into something sharp, heavy, and intentional.
                          </p>

                          <p className="mt-8 text-xl md:text-2xl text-charcoal/70 leading-relaxed font-light">
                            I’m always looking for{' '}
                            <RevealWord
                              standard="curated environments"
                              hidden="cluttercore, but chic."
                              className="text-charcoal font-semibold"
                            />
                            {' '}where the design is moving. Whether it's a dress or a city, I'm just after that{' '}
                            <RevealWord
                              standard="architecture that invites chaos,"
                              hidden="vibe that vibes & vibes,"
                              className="text-charcoal font-semibold"
                            />
                            {' '}where the freedom is the whole point of the structure.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Experience + Skills side by side */}
            <div className='grid lg:grid-cols-2 gap-16 lg:gap-24 stagger-children'>
              {/* Experience */}
              <div>
                <h3 className="eyebrow mb-12">Professional Orbit</h3>
                <div className="space-y-12">
                  {[
                    {
                      role: 'Co-founder',
                      dates: '2026 – Present',
                      org: 'DAVE-T Engineering, Technology and Trading Solutions',
                      body: (
                        <>
                          I co-lead the strategic relaunch of Dave-T, evolving a 1988 legacy into a hub for
                          <span className="text-charcoal font-medium"> climate-resilient infrastructure</span>.
                          By merging human-centred design with digital tools, we are piloting flood-monitoring
                          prototypes and gender-responsive data solutions so urban resilience leaves no one behind.
                        </>
                      ),
                    },
                    {
                      role: 'Independent',
                      dates: '2021 – Present',
                      org: 'Front-End Engineer',
                      body: 'Delivering high-fidelity UI components and digital systems. Previously led development for KreditAfrica, FluxTribe and Stitchhouse at Enyata Ghana.',
                    },
                  ].map(({ role, dates, org, body }) => (
                    <div key={org}>
                      <p className="eyebrow eyebrow-accent mb-4">{role} · {dates}</p>
                      <h4 className="text-2xl font-bold text-charcoal mb-3">{org}</h4>
                      <p className="text-lg text-charcoal/70 leading-relaxed font-light">{body}</p>
                    </div>
                  ))}
                  <div className="grid grid-cols-2 gap-8 pt-2">
                    <div>
                      <p className="eyebrow eyebrow-accent mb-4">The Board</p>
                      <h4 className="text-lg font-bold text-charcoal mb-1">FEMNET</h4>
                      <p className="text-charcoal/70 text-sm font-light">Vice Chair, Young Women</p>
                    </div>
                    <div>
                      <p className="eyebrow eyebrow-accent mb-4">Education</p>
                      <h4 className="text-lg font-bold text-charcoal mb-1">Lancaster University</h4>
                      <p className="text-charcoal/70 text-sm font-light">B.Sc. Computer Science, First Class · Lancaster Gold Award</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Skills: interactive, linked to the projects and roles they show up in */}
              <div>
                <Toolkit />
                <NavLink to={'/cv'} className='mt-10 w-fit inline-flex items-center py-3 px-10 border border-charcoal/40 text-charcoal/80 hover:bg-charcoal hover:text-white transition-all duration-700'>
                    <span className="text-[11px] uppercase tracking-[0.5em] font-bold">View CV</span>
                  </NavLink>
              </div>
            </div>
          </div>
        </section>
        {/* Works Section - Intro and Horizontal Gallery */}
        <WorksSection />
        <AllBlogs />
        <Gallery />
        {/* Contact Section */}
        <Contact />
        <Footer />
      </div> </>);
};
